import type {TillTokenizeError} from '../types';

/**
 * Maps Till error keys to user-friendly messages
 * @see https://gateway.tillpayments.com/documentation/gateway#payment-js-error-codes
 */
export const TILL_ERROR_MAP: Record<string, string> = {
  // Field validation errors
  'errors.blank': 'This field is required',
  'errors.invalid': 'Invalid value entered',
  'errors.expired': 'Card has expired',
  // System/configuration errors (from official docs)
  'errors.configuration': 'Payment configuration error',
  'errors.system': 'System error occurred, please retry',
};

/**
 * Maps Till field attributes to user-friendly labels
 */
export const TILL_FIELD_LABELS: Record<string, string> = {
  number: 'Card number',
  cvv: 'CVV',
  month: 'Expiry month',
  year: 'Expiry year',
  card_holder: 'Cardholder name',
  integration_key: 'Payment system',
};

/**
 * Format a single Till error into a user-friendly string
 */
export const formatTillError = (error: TillTokenizeError): string => {
  const fieldLabel = TILL_FIELD_LABELS[error.attribute] || error.attribute;
  const message = TILL_ERROR_MAP[error.key] || error.message;
  return `${fieldLabel}: ${message}`;
};

/**
 * Process Till tokenizer errors into formatted user-friendly messages.
 * Handles integration_key errors specially with a generic message.
 */
export const getFormattedTokeniserErrors = (errors: TillTokenizeError[]): string[] => {
  if (errors.length === 0) {
    return [];
  }

  const formattedErrors: string[] = [];

  // Check for integration key error first (system error)
  const hasIntegrationError = errors.some(e => e.attribute === 'integration_key');
  if (hasIntegrationError) {
    formattedErrors.push('Payment system temporarily unavailable. Please try again.');
  }

  // Format remaining field errors
  const fieldErrors = errors.filter(e => e.attribute !== 'integration_key');
  for (const error of fieldErrors) {
    formattedErrors.push(formatTillError(error));
  }

  return formattedErrors;
};
